import React from 'react';
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from 'react-icons/md';
import { FiArrowRightCircle } from 'react-icons/fi';
import { MonthEmergencyCall } from './MonthEmergencyCall';
import {
  Global,
  TemplateProps,
} from '../../../../constants';
import {
  Button,
  ComboBox,
  ComboBoxModel,
  DateTextBox,
  FlexLayout,
  GridLayout,
  SearchLayout,
  TextBox,
} from '../../../../components';
import { Date6Calculator, Date8 } from '../../../../utils/time';
import { SearchBinding } from '../../../../models';

/**
 * 화면
 * @window w_tb_e035w_05
 * @category 월점검 비통채크현황
 */
export const MonthEmergencyCallTemplate: React.FC<TemplateProps<MonthEmergencyCall>> = ({
  scope,
}) => (
  <FlexLayout isVertical={true}>
    <SearchLayout size={Global.LAYOUT_SEARCH_HEIGHT_1}>
      <FlexLayout size={200}>
        <span>기간</span>
        <DateTextBox
          value={scope.state.yearMon}
          textAlign="center"
          format="####년 ##월"
          onChange={(value) => {
            scope.setState({ yearMon: value },
              () => scope.onRetrieveEvent());
          }}
          onEnter={() => scope.onRetrieveEvent()}
          head={(<button
            onClick={() => {
              scope.setState({ yearMon: new Date6Calculator(scope.state.yearMon).add(-1).toString() },
                () => scope.onRetrieveEvent());
            }}
          >
            <MdKeyboardArrowLeft size={24}/>
          </button>)}
          trail={(<button
            onClick={() => {
              scope.setState({ yearMon: new Date6Calculator(scope.state.yearMon).add().toString() },
                () => scope.onRetrieveEvent());
            }}
          >
            <MdKeyboardArrowRight size={24}/>
          </button>)}
          isDisabledTrackingStateChange={true}
        elField="yearMon"
        />
      </FlexLayout>

      <FlexLayout size={300}>
        <span>검색어</span>
        <TextBox
          value={scope.state.searchQuery}
          onChange={(value) => scope.SS({ searchQuery: value })}
          onEnter={() => scope.onRetrieveEvent()}
          isDisabledTrackingStateChange={true}
        elField="searchQuery"
        />
      </FlexLayout>

      <FlexLayout size={250}>
        <span style={{ color: 'var(--color-red)' }}>부서</span>
        <TextBox
          value={scope.state.divicd}
          textAlign="center"
          onChange={(value) => scope.SS({ divicd: value })}
          isDisabledTrackingStateChange={true}
          bindSearchModal={new SearchBinding(
            scope.props.modalStore,
            'TB_JC002',
            {},
            true,
            (item) => {
              scope.SS({
                divicd: item.cd,
                divinm: item.cdnm,
              });
            },
          )}
        elField="divicd"
        />
        <TextBox
          weight={1.2}
          value={scope.state.divinm}
          readonly={true}
        elField="divinm"
        />
      </FlexLayout>

      <FlexLayout size={250}>
        <span style={{ color: 'var(--color-red)' }}>사용자</span>
        <TextBox
          value={scope.state.perid}
          textAlign="center"
          onChange={(value) => scope.SS({ perid: value })}
          onEnter={() => scope.onRetrieveEvent()}
          bindSearchModal={new SearchBinding(
            scope.props.modalStore,
            'TB_JA001',
            {},
            true,
            (item) => {
              scope.setState({
                perid: item.cd,
                pernm: item.cdnm,
              }, () => scope.onRetrieveEvent());
            },
          )}
          isDisabledTrackingStateChange={true}
        elField="perid"
        />
        <TextBox
          value={scope.state.pernm}
          weight={1.2}
          textAlign="center"
          isDisabledTrackingStateChange={true}
          readonly={true}
        elField="pernm"
        />
      </FlexLayout>

      <FlexLayout size={180}>
        <span>비통등록</span>
        <ComboBox
          textAlign="center"
          value={scope.state.telcomp}
          data={[
            { value: '%', remark: '전체' },
            { value: '0', remark: '미등록' },
            { value: '1', remark: '등록' },
          ].map((y) => new ComboBoxModel(y.value, y.remark))}
          onSelect={(option) => scope.SS({ telcomp: option.value })}
        elField="telcomp"
        />
      </FlexLayout>

      <FlexLayout size={180}>
        <span>수신결과</span>
        <ComboBox
          textAlign="center"
          value={scope.state.susin}
          data={[
            { value: '%', remark: '전체' },
            { value: '0', remark: '미수신' },
            { value: '1', remark: '수신' },
          ].map((y) => new ComboBoxModel(y.value, y.remark))}
          onSelect={(option) => scope.SS({ susin: option.value })}
        elField="susin"
        />
      </FlexLayout>

      <FlexLayout size={180}>
        <span>AI결과</span>
        <ComboBox
          textAlign="center"
          value={scope.state.result}
          data={[
            { value: '%', remark: '전체' },
            { value: '0', remark: '미처리' },
            { value: '1', remark: '불량' },
            { value: '2', remark: '완료' },
            { value: '3', remark: '끊킴' },
          ].map((y) => new ComboBoxModel(y.value, y.remark))}
          onSelect={(option) => scope.SS({ result: option.value })}
        elField="result"
        />
      </FlexLayout>
    </SearchLayout>

    <FlexLayout>
      <FlexLayout isVertical={true} size={980}>
        <FlexLayout size={30}>
          <FlexLayout
            justify="start"
            align="center"
            size={100}
            style={{ color: 'var(--color-blue)' }}
          >
            *최근내역
          </FlexLayout>
          <FlexLayout
            justify="start"
            align="center"
          >
            (리스트에 AI결과는 해당월에 가장 마지막에 연결된 내역이 뜨게 됩니다)
          </FlexLayout>
        </FlexLayout>
        <GridLayout
          ref={scope.grid}
          header={[
            {
              id: 'actcd',
              text: '현장코드',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {x.actcd}
                </FlexLayout>
              ),
            },
            {
              id: 'actnm',
              text: '현장명',
              width: 180,
              render: (x) => (
                <FlexLayout
                  justify="left"
                  align="center"
                >
                  {x.actnm}
                </FlexLayout>
              ),
            },
            {
              id: 'telcomp',
              text: '비통등록',
              width: 60,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                  style={
                  x.telcomp === '0' ? { color: 'var(--color-red)' }
                    : x.telcomp === '1' ? { color: 'var(--color-blue)' } : null
                }
                >
                  {
                    x.telcomp === '0' ? 'X'
                      : x.telcomp === '1' ? 'O' : ''
                  }
                </FlexLayout>
              ),
            },
            {
              id: 'tel',
              text: '전화번호',
              width: 120,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {x.tel}
                </FlexLayout>
              ),
            },
            {
              id: 'divinm',
              text: '부서',
              width: 100,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {x.divinm}
                </FlexLayout>
              ),
            },
            {
              id: 'pernm',
              text: '담당자',
              width: 100,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {x.pernm}
                </FlexLayout>
              ),
            },
            {
              id: 'susinresult',
              text: '수신결과',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                  style={
                    x.susinresult === '0' ? { color: 'var(--color-red)' }
                      : x.susinresult === '1' ? { color: 'var(--color-blue)' } : null
                  }
                >
                  {
                    x.susinresult === '0' ? 'X'
                      : x.susinresult === '1' ? 'O' : ''
                  }
                </FlexLayout>
              ),
            },
            {
              id: 'susincnt',
              text: '수신횟수',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="start"
                  align="center"
                >
                  {x.susincnt}
                </FlexLayout>
              ),
            },
            {
              id: 'result',
              text: 'AI결과',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                  style={
                    x.result === '0' ? { color: 'var(--color-red)' }
                      : x.result === '2' ? { color: 'var(--color-blue)' }
                        : x.result === '3' ? { color: 'var(--color-violet)' } : ''
                  }
                >
                  {
                    x.result === '0' ? '미처리'
                      : x.result === '1' ? '불량'
                        : x.result === '2' ? '완료'
                          : x.result === '3' ? '끊킴' : ''
                  }
                </FlexLayout>
              ),
            },
            {
              id: 'cnt',
              text: 'AI횟수',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="start"
                  align="center"
                >
                  {x.cnt}
                </FlexLayout>
              ),
            },
          ]}
          rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
          data={scope.state.recentList}
          onRowClick={(x) => scope.onRowFocusEvent(x)}
        elGrid="recentList"
        >
        </GridLayout>
      </FlexLayout>

      <FlexLayout isVertical={true}>
        <FlexLayout
          size={30}
          justify="start"
          align="center"
          style={{ color: 'var(--color-blue)' }}
        >
          *상세내역
        </FlexLayout>
        <GridLayout
          ref={scope.grid2}
          header={[
            {
              id: 'callnum',
              text: '전화번호',
              width: 100,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {x.callnum}
                </FlexLayout>
              ),
            },
            {
              id: 'calldate',
              text: '수신일자',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {Date8.withDash(x.calldate)}
                </FlexLayout>
              ),
            },
            {
              id: 'calltime',
              text: '수신시간',
              width: 80,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                >
                  {x.calltime.substr(0, 2)}:{x.calltime.substr(2, 2)}:{x.calltime.substr(4, 2)}
                </FlexLayout>
              ),
            },
            {
              id: 'sizeflag',
              text: '녹음',
              width: 180,
              render: (x) => (
                <FlexLayout>
                  <FlexLayout
                    justify="center"
                    align="center"
                    size={160}
                  >
                    {x.rec_recedate && <audio
                      style={{ height: 24, width: 160 }}
                      src={`https://php-api.elmansoft.com/callrec/call_rec_download.php?custcd=${scope.props.publicStore.user.custcd}&spjangcd=${scope.props.publicStore.user.spjangcd}&recedate=${x.rec_recedate}&recenum=${x.rec_recenum}`}
                      controls
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      preload="none"
                    >
                    </audio>}
                  </FlexLayout>

                  <FlexLayout
                    justify="left"
                    align="center"
                    style={
                    x.sizeflag === '0' ? { color: 'var(--color-red)' }
                      : x.sizeflag === '1' ? { color: 'var(--color-blue)' } : ''
                  }
                    size={50}
                  >
                    {
                      x.sizeflag === '0' ? '파일없음'
                        : x.sizeflag === '1' ? '파일있음' : '' }
                  </FlexLayout>
                </FlexLayout>
              ),
            },
            {
              id: 'sttflag',
              text: '통화녹취록',
              width: 80,
              render: (x) => (
                <FlexLayout>
                  <FlexLayout
                    justify="center"
                    align="center"
                    size={40}
                  >
                    <Button
                      onClick={() => scope.recordFileBtn()}
                      isIcon={true}
                    >
                      <FiArrowRightCircle size={17} />
                    </Button>
                  </FlexLayout>
                  <FlexLayout
                    justify="left"
                    align="center"
                    style={
                      x.sttflag === '0' ? { color: 'var(--color-red)' }
                        : x.sttflag === '2' ? { color: 'var(--color-blue)' } : ''
                    }
                  >
                    {
                      x.sttflag === '0' ? '요청없음'
                        : x.sttflag === '1' ? '텍스트변환중'
                          : x.sttflag === '2' ? '있음' : ''
                    }
                  </FlexLayout>
                </FlexLayout>
              ),
            },
            {
              id: 'result',
              text: 'AI결과',
              width: 60,
              render: (x) => (
                <FlexLayout
                  justify="center"
                  align="center"
                  style={
                    x.result === '0' ? { color: 'var(--color-red)' }
                      : x.result === '2' ? { color: 'var(--color-blue)' }
                        : x.result === '3' ? { color: 'var(--color-violet)' } : ''
                  }
                >
                  {
                    x.result === '0' ? '미처리'
                      : x.result === '1' ? '불량'
                        : x.result === '2' ? '완료'
                          : x.result === '3' ? '끊킴' : ''
                  }
                </FlexLayout>
              ),
            },
          ]}
          rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
          data={scope.state.detailList}
        elGrid="detailList"
        >
        </GridLayout>
      </FlexLayout>
    </FlexLayout>
  </FlexLayout>
);
